import { ChronocodeVO as BaseChronocodeVO, IVo, entity, field } from '@wizeoo/akm-compute-lib';
import { TimeGapVO } from './timegap.vo';
import { ChronoCodeLevelVO } from './chronocodelevel.vo';
import { ChronoCode, ChronoCodeLevel, TranslatedEmb } from '@wizeoo/akm-message-lib';
import { WizDateUtils } from '@wizeoo/akm-core-lib';
import { Model } from '../../model/model';
import { TranslatableLabelVO } from '../common/translation/translatable-label.vo';
import { ViewData } from '../../model/view.data';

/**
 * Codification temporelle
 */
@entity('wizeoo.allkeyme.core.message.ChronoCode', 'BU')
export class ChronocodeVO extends BaseChronocodeVO implements IVo {

  public get model(): Model {
    return this._model as Model;
  }

  constructor(model: Model, pSource?: ChronoCode) {
    super(model, pSource);
  }

  public set domain(domain: string) {
    this.source.domain = domain;
  }

  public get domain(): string {
    return this.source.domain;
  }

  @field('string')
  public get businessUnitCode(): string {
    return this.source.businessUnitCode;
  }

  public set businessUnitCode(businessUnitCode: string) {
    this.source.businessUnitCode = businessUnitCode;
  }
  public get uid(): string {
    return this.source.uid;
  }
  public set uid(uid: string) {
    this.source.uid = uid;
  }

  public get level(): number {
    return this.source.level;
  }

  public set level(level: number) {
    this.source.level = level;
  }

  public get id(): number {
    return this.source.id;
  }

  public set id(id: number) {
    this.source.id = id;
  }

  @field('string')
  public get parentCode(): string {
    return this.source.parentCode;
  }

  public set parentCode(parentCode: string) {
    this.source.parentCode = parentCode;
  }

  @field('TranslatedEmb')
  public set label(value: TranslatedEmb) {
    this.source.label = value;
    this._labelVO = null;
  }
  public get label(): TranslatedEmb {
    return this.source.label;
  }

  @field('string')
  public get rootCode(): string {
    return this.source.rootCode;
  }

  public set rootCode(rootCode: string) {
    this.source.rootCode = rootCode;
  }

  @field('string')
  public get code(): string {
    return this.source.code;
  }

  public set code(code: string) {
    this.source.code = code;
  }

  @field('string')
  public get shortCode(): string {
    return this.source.shortCode;
  }

  public set shortCode(shortCode: string) {
    this.source.shortCode = shortCode;
  }

  @field('number')
  public get endDate(): number {
    return this.source.endDate;
  }

  public set endDate(endDate: number) {
    this.source.endDate = endDate;
  }

  @field('number')
  public get startDate(): number {
    return this.source.startDate;
  }

  public set startDate(startDate: number) {
    this.source.startDate = startDate;
  }

  /**
   * Regex pour les enfants
   */
  @field('string')
  public get childrenRegex(): string {
    return this.source.childrenRegex;
  }
  public set childrenRegex(str: string) {
    this.source.childrenRegex = str;
  }

  @field('boolean')
  public get deletable(): boolean {
    return this.source.deletable;
  }

  public set deletable(deletable: boolean) {
    this.source.deletable = deletable;
  }

  @field('boolean')
  public get enabled(): boolean {
    return this.source.enabled;
  }

  public set enabled(enabled: boolean) {
    this.source.enabled = enabled;
  }

  @field('boolean')
  public get order(): number {
    return this.source.order;
  }

  public set order(order: number) {
    this.source.order = order;
  }

  public set nbDays(nbDays: number) {
    this.source.nbDays = nbDays;
  }

  public set nbSaturdays(nbSaturdays: number) {
    this.source.nbSaturdays = nbSaturdays;
  }

  public set nbSundays(nbSundays: number) {
    this.source.nbSundays = nbSundays;
  }


  // TODO : détecter les changements sur les sous-collections
  @field('maintenanceFlag') // patch pour s'assurer de la demande de mise à jour sur modif des libellés de levels
  public get maintenanceFlag(): string {
    return this.source.maintenanceFlag;
  }
  public set maintenanceFlag(value: string) {
    this.source.maintenanceFlag = value;
  }

  /**
   * Nombre de jours, pour affichage
   * @return string Nombre de jours en chaine de caractères
   */
  public get nbDaysStr(): string {
    return !!this.source.nbDays ? this.source.nbDays.toString() : '0';
  }

  /**
   * Nombre de samedis, pour affichage
   * @return string Nombre de samedis en chaine de caractères
   */
  public get nbSaturdaysStr(): string {
    return !!this.source.nbSaturdays ? this.source.nbSaturdays.toString() : '0';
  }

  /**
   * Nombre de dimanches, pour affichage
   * @return string Nombre de dimanches en chaine de caractères
   */
  public get nbSundaysStr(): string {
    return !!this.source.nbSundays ? this.source.nbSundays.toString() : '0';
  }

  /**
   * Override children attribut
   * This overload allows to cast a map into an array of chronocodes
   */
  private _children: ChronocodeVO[] = [];

  get children(): ChronocodeVO[] {
    return this._children;
  }

  /**
   * This overload allows to cast a map into an array of chronocodes
   * @param value
   */
  set children(value: ChronocodeVO[]) {
    this._children = value;
  }

  /**
   * Ajout de chronocode enfant
   * @param value enfant ajouté
   */
  public addChild(value: ChronocodeVO) {
    this.children.push(value);
  }

  /**
   * Suppression dde chronocode enfant
   * @param value enfant supprimé
   */
  public removeChild(value: ChronocodeVO) {
    this.children.forEach((child, index) => {
      if (child.code === value.code) {
        this.children.splice(index, 1);
      }
    });
  }

  /**
   * Le parent de niveau 1
   */
  public get parentRootVO(): ChronocodeVO {
    let base: any = this.model.chronocodeEngine.getChronocode(this.parentRootCode);
    return base ? base as ChronocodeVO : null;
  }

  /** Accesseur au vo du niveau */
  private _levelVO: ChronoCodeLevelVO;
  public get levelVO(): ChronoCodeLevelVO {
    if (!this._levelVO) {
      if (this.parentRootCode) {
        const rootParent: any = this.model.chronocodeEngine.getChronocode(this.parentRootCode);
        if (rootParent) {
          this._levelVO = (rootParent as ChronocodeVO).chronoCodeLevelVOs[this.level];
        }
      }
    }
    return this._levelVO;
  }
  public set levelVO(pLevelVO: ChronoCodeLevelVO) {
    this._levelVO = pLevelVO;
  }

  /** Libellé du niveau */
  public get levelStr(): string {
    let result: string = '';
    if (this.level) {
      const levelVO: ChronoCodeLevelVO = this.levelVO;
      if (levelVO) {
        result = levelVO.trsLabel;
      } else {
        result = this.level.toString();
      }
    }
    return result;
  }

  /**
   * Return translated label
   */
  public get trsLabel(): string {
    return this.model.localeEngine.translate(this.label, this.code);
  }

  /** Chronocode parent */
  private _parentVO: ChronocodeVO;
  public get parentVO(): ChronocodeVO {
    return this.getParentVO();
  }
  protected getParentVO(): ChronocodeVO {
    if (!this._parentVO) {
      let base: any = this.model.chronocodeEngine.getChronocode(this.parentCode);
      if(base) {
        this._parentVO = base as ChronocodeVO;
      }
    }
    return this._parentVO;
  }

  /** Acces au vo time gap */
  public get timeGapVO(): TimeGapVO {
    return this.getTimeGapVO();
  }
  protected getTimeGapVO(): TimeGapVO {
    const level: ChronoCodeLevelVO = this.levelVO;
    return level ? this.model.chronocodeEngine.getTimeGap(level.timeGapCode) : null;
  }

  /** Libellé traductible */
  private _labelVO: TranslatableLabelVO;
  public get labelVO(): TranslatableLabelVO {
    if (!this._labelVO) {
      this._labelVO = this.model.localeEngine.newTranslatableVO(this, 'label');
    }
    return this._labelVO;
  }

  /** Le chronocode est-il persisté? */
  public get exist(): boolean {
    // l'objet existe en BDD (id)
    return this.source.id > 0;
  }

  public get deprecated(): boolean {
    return this.source.deprecated;
  }
  public set deprecated(value: boolean) {
    this.source.deprecated = value;
  }

  public get deleted(): boolean {
    return this.source.deleted;
  }
  public set deleted(value: boolean) {
    this.source.deleted = value;
  }

  /** Poids temporel minimal (le mien ou le minimal de mes parents si undefined) */
  public get minTemporalWeight(): number {
    return this.getMinTemporalWeight();
  }
  protected getMinTemporalWeight(): number {
    let result: number;
    const timeGapVO: TimeGapVO = this.timeGapVO;
    if (timeGapVO) {
      result = timeGapVO.temporalWeight;
    }
    if (!result && this.parentVO) {
      result = this.parentVO.minTemporalWeight;
    }
    return result;
  }

  /** Suis-je EON? */
  // public get isRoot(): boolean {
  //   return !this.parentVO;
  // }

  /** Suis-je utilisé? */
  private _used: boolean;
  public get isUsed(): boolean {
    return this.getUsed();
  }
  public set isUsed(b: boolean) {
    this.setUsed(b);
  }
  protected getUsed(): boolean {
    return this._used;
  }
  protected setUsed(b: boolean): void {
    this._used = b;
  }

  /** Affichage label + dates */
  public get labelAndDates(): string {
    if (this.code === ViewData.defaultChronoCode) {
      // c'est EON
      return '';
    }
    return this.trsLabel + ' ' + this.datesStr;
  }

  /** Affichage dates début - fin */
  public get datesStr(): string {
    return this.startDateStr + ' - ' + this.endDateStr;
  }

  /** Code du parent de niveau 1 pour filtre */
  public parentRootCode: string;

  /**
   * Code du parent dans le contexte "sous-arbre" (EON ignoré)
   */
  public get subTreeParentCode(): string {
    return this.level === 1 ? undefined : this.parentCode;
  }

  /* ################################################
         gestion des libellés de niveaux
   ################################################ */

   public get startDateStr(): string {
    return WizDateUtils.numberToLocalizedString(this.startDate);
  }

  public get endDateStr(): string {
    return WizDateUtils.numberToLocalizedString(this.endDate);
  }

  /** liste des levels */
  private _levelsVO: ChronoCodeLevelVO[] = null;
  /** map des levels par level */
  public _levelVOMap: Map<number, ChronoCodeLevelVO> = null;

  private initLevels(): void {
    if (!this._levelsVO && !!this.source.chronoCodeLevels) {
      this._levelVOMap = new Map();
      this._levelsVO = Object.values(this.source.chronoCodeLevels).map((chrol) => {
        const lvlVO = new ChronoCodeLevelVO(this.model, chrol);
        this._levelVOMap.set(lvlVO.level, lvlVO);
        return lvlVO;
      });
    }
  }

  /** Les niveaux de chronocodes */
  public get chronoCodeLevelVOs(): { [key: string]: ChronoCodeLevelVO } {
    const result: { [key: string]: ChronoCodeLevelVO } = {};
    if (this.source.chronoCodeLevels) {
      Object.values(this.source.chronoCodeLevels).forEach((level: ChronoCodeLevel) => {
        result[level.level] = new ChronoCodeLevelVO(this.model, level);
      });
    }
    return result;
  }

  /**
   * Ajout d'un niveau de chronocode (depuis engine)
   * @param levelVO Le niveau
   */
  public addLevel(levelVO: ChronoCodeLevelVO): void {
    if (!this.source.chronoCodeLevels) {
      this.source.chronoCodeLevels = {};
    }
    this.source.chronoCodeLevels[levelVO.level] = levelVO.source;
    this.chronoCodeLevelVOs[levelVO.level] = levelVO;
  }

  /** Accès à la collection de level */
  public get levelsVO(): ChronoCodeLevelVO[] {
    // on initialise les levels pour être iso avec le src
    this.initLevels();
    return this._levelsVO;
  }

  public get levelsVOMap(): Map<number, ChronoCodeLevelVO> {
    this.initLevels();
    return this._levelVOMap;
  }

  /**
   * retourne le libellé avec le parent en suffixe
   */
  public get trsLabelWithParent(): string {
    let result: string = this.model.localeEngine.translate(this.label, this.code);
    if (this.parentVO) {
      result = result + ' (' + this.parentVO.trsLabel + ')';
    }
    return result;
  }

  /* ################################################
        création / simulation
   ################################################ */

  public simulatedParent: ChronocodeVO = null; // parent temporaire lors de la simulation (création)
  public template: any = null; // ChronocodeTemplateVO = null; // pour simulation, template de rattachement
}
